import { ApolloError, useLazyQuery } from "@apollo/client";
import React, { useEffect, useState } from "react";
import { IAddress, IUser, IUserState } from "../../../../reducers/user/types";
import { useSnackBar } from "../../../common/SnackBarContext/SnackBarContext";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { IAppState } from "../../../../store";
import { GET_ALL_USER_ROLE_DRIVER } from "../../../../graphql/vehicleMovement/queries";
import { formatGraphQLErrorMessage, getLocalisedText, LocaliseFormat, LocaliseType } from "../../../common/utils";
import { SnackBarVariant } from "../../../common/SnackbarWrapper/SnackbarWrapper";
import { MUIDataTableOptions } from "mui-datatables";
import { SelectableRows } from "../../../common/NuvvenTable/types";
import { CircularProgress, CssBaseline, Grid, Typography } from "@mui/material";
import { NuvvenTable } from "../../../common/NuvvenTable/NuvvenTable";


export interface IDriverProfile {
  id?: string
  firstName: string,
  lastName: string,
  dateOfBirth: string,
  email: string,
  phoneNumber: IPhoneNumber,
  license: IDriverLicenseDetails,
  documents: IDriverDocument[],
  profilePicture: string,
  address: IAddress
  nationalInsuranceNumber?: string
}

export interface IDriverLicenseDetails {
  issuingAuthority: string;
  licenseNumber: string;
  dateOfExpiry: string;
  dateOfIssue: string;
  endorsement: string;
  frontImage: string;
  backImage: string;
  offences?: number;
  points?: number;
  dvlaVerificationStatus?: string;
}

export interface IDriverDocument {
  id?: string;
  title: string;
  expiryDate: string;
  documentType: string;
  reminderBefore?: number;
  documentName: string;
  documentKey: string;
  createdAt?: string;
}

export interface IPhoneNumber {
  phone: string;
  country: string;
}


const DriverTable = () => {
  const snackbar = useSnackBar();
  const navigate = useNavigate();
  const userState = useSelector((state: IAppState) => state.userReducer);
    const { country } = userState.currentOrganisation.address;

  const [rows, setRows] = useState<IDriverProfile[]>([]);

  const [getDrivers, { loading: isDriverLoading }] = useLazyQuery(GET_ALL_USER_ROLE_DRIVER, {
    fetchPolicy: "network-only",
    onCompleted(data) {
      if (data && data.getDriverUsersInBranch.length) {
        const reshapeData = data.getDriverUsersInBranch.map((data: IUserState) => reshapeDriverData(data))
        setRows(reshapeData)
      }

    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    }
  })



  useEffect(() => {
    if (userState.tenancy) {
      getDrivers();
    }
  }, [userState]);

  function reshapeDriverData(driver: IUserState) {
    if(country == "United Kingdom"){
      return {
        id: driver.id,
        driverName: `${driver.firstName} ${driver.lastName}`,
        email: driver.email,
        licenseNumber: driver.driverProfile?.license?.licenseNumber || "",
        dvlaVerificationStatus: driver.driverProfile?.license?.dvlaVerificationStatus || ""
      };
    }
    return {
      id: driver.id,
      driverName: `${driver.firstName} ${driver.lastName}`,
      email: driver.email,
      licenseNumber: driver.driverProfile?.license?.licenseNumber || "",
    };
  }
  const column: any = [
    {
      label: "Name",
      name: "driverName"
    },
    {
      label: "Email Address",
      name: "email"
    },
    {
      label: `${getLocalisedText(country, LocaliseType.LICENSE, LocaliseFormat.CAPITALIZED)} Number`,
      name: "licenseNumber"
    },
  ];

  if(country == "United Kingdom"){
    column.push({
      label: "DVLA Verification Status",
      name: "dvlaVerificationStatus"
    })
  }

  const options: MUIDataTableOptions = {
    selectableRows: SelectableRows.NONE,
    onRowClick: (rowData: any, rowMeta: { dataIndex: number, rowIndex: number }) => {
      if (rowData && rowData.length) {
        const row = rows[rowMeta.dataIndex];
        const rowId = row.id
        if (rowId) {
          navigate(`/update-driver?id=${rowId}`)
        }
      }
    },
    textLabels: {
      body: {
        toolTip: "Sort",
        noMatch: isDriverLoading ?
          'Loading...' :
          'Sorry, there is no matching data to display',
      },
      pagination: {
        next: "Next Page",
        previous: "Previous Page",
        rowsPerPage: "Rows per page:",
        displayRows: "of",
      },
      toolbar: {
        search: "Search",
        downloadCsv: "Download CSV",
        print: "Print",
        viewColumns: "View Columns",
        filterTable: "Filter Table",
      },
      filter: {
        all: "All",
        title: "FILTERS",
        reset: "RESET",
      },
      viewColumns: {
        title: "Show Columns",
        titleAria: "Show/Hide Table Columns",
      },
      selectedRows: {
        text: "row(s) selected",
        delete: "Delete",
        deleteAria: "Delete Selected Rows",
      },
    }
  }


  return (<Grid container spacing={2}>
    <CssBaseline />
    <Grid container item xs={6}>
      <Typography variant="h1" color="primary">
        Drivers
      </Typography>
    </Grid>
    <Grid container item xs={12}>
      {!isDriverLoading ? (
        <NuvvenTable
          title=""
          rows={rows}
          columns={column}
          options={options}
          setSelection={() => { }}
        />
      ) : (
        <CircularProgress />
      )}
    </Grid>
  </Grid>
  )
};

export default DriverTable;